/**
 * ContentPlanPlatformRoutePage — route-level wrapper for one author platform.
 *
 * Mounted at `/project/:projectId/content-plan/platform/:platformId`.
 * The selected author is URL-scoped through `?author=:authorId`, matching the
 * main Content Plan workspace. This route intentionally does not expand the
 * platform editor inline inside the Platforms overview.
 */

import React from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LanguageIcon from '@mui/icons-material/Language';
import { useAuth } from '@frontend/contexts/AuthContext';
import { useProjectCapability } from '@modules/billing-access';
import { canAccessContentPlan, isContentPlanAdminOnly } from '@modules/content-plan/config/access';
import { useCurrentAuthor } from '@modules/content-plan/hooks/useAuthor';
import { useAuthorPlatforms } from '@modules/content-plan/hooks/useAuthorPlatforms';
import { resolvePlatformOptimalFieldSlots } from '@modules/content-plan/utils/strategyCadence';
import {
  normalizePlatformWorkspaceTab,
  PlatformWorkspace,
  type PlatformWorkspaceTabKey,
} from '@modules/content-plan/components/platforms/PlatformWorkspace';

const TESTID = 'content-plan-platform-workspace';

function LoadingState() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <CircularProgress />
      <Typography sx={{ ml: 2 }}>Loading…</Typography>
    </Box>
  );
}

export const ContentPlanPlatformRoutePage: React.FC = () => {
  const { projectId, platformId } = useParams<{ projectId: string; platformId: string }>();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const requestedAuthorId = params.get('author');
  const { currentUser, isAdmin, adminLoaded } = useAuth() as {
    currentUser: { uid: string } | null;
    isAdmin: boolean;
    adminLoaded: boolean;
  };
  const contentPlanAccess = useProjectCapability(projectId, 'content_plan.access');
  const authorState = useCurrentAuthor(projectId);
  const workspaceAuthorId = authorState.isOverview ? null : authorState.authorId;
  const { platforms, loading: platformsLoading } = useAuthorPlatforms(projectId ?? null, workspaceAuthorId, 'authors');

  if (!projectId || !platformId) {
    return <Navigate to="/all-projects" replace />;
  }

  if (!currentUser || (isContentPlanAdminOnly() && (!adminLoaded || contentPlanAccess.loading))) {
    return <LoadingState />;
  }

  if (!canAccessContentPlan({ isAdmin, hasBillingAccess: contentPlanAccess.allowed })) {
    return <Navigate to="/all-projects" replace />;
  }

  const backUrl = requestedAuthorId && requestedAuthorId !== 'all'
    ? `/project/${projectId}/content-plan?author=${encodeURIComponent(requestedAuthorId)}&cpTab=platforms`
    : `/project/${projectId}/content-plan?cpTab=platforms`;

  const renderGuard = (message: string) => (
    <Box sx={{ px: '24px' }}>
      <Paper
        variant="outlined"
        sx={{ p: 3, mt: 3 }}
        data-testid={TESTID}
      >
        <Stack spacing={2}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(backUrl)}
            sx={{ alignSelf: 'flex-start' }}
            data-testid="content-plan-platform-workspace-back"
          >
            Back to platforms
          </Button>
          <Alert severity="info">{message}</Alert>
        </Stack>
      </Paper>
    </Box>
  );

  if (!requestedAuthorId) {
    return renderGuard('Pick a single author before opening platform settings.');
  }
  if (requestedAuthorId === 'all') {
    return renderGuard('Platform settings are available only for one selected author.');
  }
  if (authorState.loading) {
    return <LoadingState />;
  }
  if (!authorState.author || !workspaceAuthorId) {
    return renderGuard('The selected author is no longer available.');
  }
  if (platformsLoading) {
    return <LoadingState />;
  }

  const platform = platforms.find((item) => item.id === platformId);
  if (!platform) {
    return renderGuard(`Platform "${platformId}" is not enabled for this author.`);
  }
  if (platform.isActive === false) {
    return renderGuard(`Platform "${platform.displayName ?? platform.id}" is disabled.`);
  }

  const optimalSlots = resolvePlatformOptimalFieldSlots(platform);
  const tabKey = normalizePlatformWorkspaceTab(params.get('tab'));
  const handleTabChange = (nextTab: PlatformWorkspaceTabKey) => {
    const next = new URLSearchParams(params);
    next.set('tab', nextTab);
    setParams(next, { replace: false });
  };

  return (
    <Box sx={{ px: '24px' }}>
      <Paper
        variant="outlined"
        sx={{ p: 3, mt: 3 }}
        data-testid={TESTID}
      >
        <Stack spacing={2.5}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(backUrl)}
                data-testid="content-plan-platform-workspace-back"
              >
                Back
              </Button>
              <Box sx={{ display: 'flex', color: 'text.secondary' }}>
                <LanguageIcon fontSize="small" />
              </Box>
              <Box>
                <Typography variant="h5">{platform.displayName ?? platform.id}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Platform workspace for {authorState.author.displayName ?? authorState.author.id}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              <Chip size="small" color="success" label="active" />
              {typeof platform.maxPostLength === 'number' && (
                <Chip size="small" variant="outlined" label={`max ${platform.maxPostLength} chars`} />
              )}
              {optimalSlots.length > 0 && (
                <Chip size="small" variant="outlined" label={`${optimalSlots.length} optimal slots`} />
              )}
              <Chip size="small" variant="outlined" label={`tab: ${tabKey}`} />
            </Stack>
          </Stack>

          <Box data-testid="content-plan-platform-workspace-shell">
            <PlatformWorkspace
              projectId={projectId}
              authorId={workspaceAuthorId}
              platform={platform}
              userId={currentUser.uid}
              activeTab={tabKey}
              onTabChange={handleTabChange}
            />
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default ContentPlanPlatformRoutePage;
